// components/entities/tables/campaigns/CampaignsTable.tsx
'use client';

import React, { useMemo } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { CampaignFilters, InventoryEntityState } from '#types/types';
import { Table, TableColumnDef, type TableFilterOption } from '../../../portal/Table';
import { Activity, BarChart3, Clock, Landmark, Layers, Target } from '@/components/ui/lucide-memo';

/**
 * One campaign list, at every granularity.
 *
 * Replaces the three tables that each read one legacy table and could therefore only ever answer
 * about their own granularity. `targetKind` is a locked filter here, not a choice of component, so
 * a caller that wants only portfolio runs asks for them instead of picking a different table.
 */

const translations = {
  en: {
    sortCreatedAt: 'Created date',
    sortUpdatedAt: 'Last updated',
    campaign: 'Campaign',
    granularity: 'Scope',
    status: 'Status',
    targets: 'Target brands',
    results: 'Results',
    updatedAt: 'Updated',
  },
  fr: {
    sortCreatedAt: 'Date creation',
    sortUpdatedAt: 'Derniere maj',
    campaign: 'Campagne',
    granularity: 'Portee',
    status: 'Statut',
    targets: 'Marques ciblees',
    results: 'Resultats',
    updatedAt: 'Maj',
  },
} as const;

/** Module-scope: the row closes over nothing, so it stays one reference for the whole app and the
 *  memoized row wrappers inside <Table> survive every re-render of this component. */
const renderCampaignRow = (
  campaign: InventoryEntityState<'Campaign'>,
  isExpanded: boolean,
  toggle: () => void,
  currentColumns?: TableColumnDef[],
) => (
  <div className="flex w-full flex-none flex-col">
    <EntityView
      type="Campaign"
      entityId={campaign.id}
      variant="row"
      onClick={toggle}
      columns={currentColumns}
      className={isExpanded ? 'bg-white/[0.02] border-white/10' : ''}
    />
    {isExpanded && (
      <EntityView
        type="Campaign"
        entityId={campaign.id}
        variant="page"
        pageHasEntityRowAbove
        className="h-auto border-b border-[#2A2A2A]"
      />
    )}
  </div>
);

interface CampaignsTableProps {
  initialFilters?: Partial<CampaignFilters>;
  lockedFilters?: Partial<CampaignFilters>;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  renderCustomToolbarHeader?: () => React.ReactNode;
  /** Hidden when a caller has already locked the list to one granularity, where it says nothing. */
  showGranularityColumn?: boolean;
}

export const CampaignsTable = React.memo(({
  initialFilters,
  lockedFilters,
  assetLedgerTitle = 'Campaigns',
  assetLedgerBreadcrumb = 'Source / Campaigns',
  renderCustomToolbarHeader,
  showGranularityColumn,
}: CampaignsTableProps) => {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  const sortOptions = useMemo(
    () => [
      { label: t.sortCreatedAt, field: 'createdAt' },
      { label: t.sortUpdatedAt, field: 'updatedAt' },
    ],
    [t.sortCreatedAt, t.sortUpdatedAt]
  );

  const filterOptions = useMemo<TableFilterOption<'Campaign'>[]>(
    () => [{ key: 'status', label: t.status }],
    [t.status]
  );

  // A list already locked to one granularity would show the same word on every row.
  const withGranularity = showGranularityColumn ?? !lockedFilters?.targetKind;

  const columns = useMemo<TableColumnDef[]>(() => [
    { id: 'campaign', icon: Target, minWidth: 250, header: t.campaign },
    ...(withGranularity ? [{ id: 'granularity', icon: Layers, minWidth: 100, header: t.granularity }] : []),
    { id: 'status', icon: Activity, minWidth: 120, header: t.status },
    { id: 'targets', icon: Landmark, minWidth: 100, header: t.targets, align: 'right' as const },
    { id: 'results', icon: BarChart3, minWidth: 120, header: t.results, align: 'right' as const },
    { id: 'updatedAt', icon: Clock, minWidth: 150, header: t.updatedAt },
    { id: 'actions', minWidth: 240, header: '', align: 'right' as const },
  ], [t, withGranularity]);

  return (
    <Table
      entityType="Campaign"
      initialFilters={initialFilters}
      lockedFilters={lockedFilters}
      filterOptions={filterOptions}
      columns={columns}
      assetLedgerTitle={assetLedgerTitle}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb}
      sortOptions={sortOptions}
      renderToolbarExtras={renderCustomToolbarHeader}
      renderRow={renderCampaignRow}
    />
  );
});

CampaignsTable.displayName = 'CampaignsTable';
